import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { motion } from "framer-motion";
import { 
  BarChart3, TrendingUp, Calendar as CalendarIcon, Building2, Layers, 
  ShoppingCart, Download, RotateCcw, Filter, PieChart, GitCompare, 
  ChevronDown, X, Plus, Eye, ArrowRight, Check, Flame, Snowflake,
  Target, Users, Image, Package, ArrowUp, ArrowDown, Minus
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, Cell, PieChart as RePieChart, Pie, LineChart, Line, 
  AreaChart, Area, ComposedChart, ReferenceLine
} from "recharts";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { MultiSelectFilter } from "../components/ui/multi-select-filter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== UTILS ====================
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);
};

const formatK = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M€`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k€`;
  return `${value?.toFixed(0) || 0}€`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "-";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

// Format date to French format (DD/MM/YYYY)
const formatDateFR = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const CATEGORY_COLORS = {
  'Hébergement/Sauvegarde': '#3b82f6', 'Téléphonie': '#22c55e', 'Fibre/Internet': '#06b6d4',
  'Commission': '#f59e0b', 'Installation': '#8b5cf6', 'Matériel': '#ec4899', 'Autre': '#64748b'
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b', '#f97316'];

// ==================== DATE PICKER COMPONENT ====================
const DateRangePicker = ({ startDate, endDate, onStartChange, onEndChange, label }) => {
  return (
    <div className="flex flex-col gap-1">
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-32 justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(new Date(startDate), "dd/MM/yyyy") : "Début"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate ? new Date(startDate) : undefined}
              onSelect={(date) => onStartChange(date ? format(date, "yyyy-MM-dd") : "")}
              locale={fr}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-32 justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(new Date(endDate), "dd/MM/yyyy") : "Fin"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate ? new Date(endDate) : undefined}
              onSelect={(date) => onEndChange(date ? format(date, "yyyy-MM-dd") : "")}
              locale={fr}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

// ==================== QUICK DATE BUTTONS (Modern Chips) ====================
const QuickDateButtons = ({ onSelect, activePreset }) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const presets = [
    { key: "this_month", label: "Ce mois", start: `${year}-${String(month + 1).padStart(2, '0')}-01`, end: format(now, "yyyy-MM-dd") },
    { key: "last_month", label: "Mois dernier", start: `${month === 0 ? year - 1 : year}-${String(month === 0 ? 12 : month).padStart(2, '0')}-01`, end: `${month === 0 ? year - 1 : year}-${String(month === 0 ? 12 : month).padStart(2, '0')}-${new Date(month === 0 ? year - 1 : year, month === 0 ? 12 : month, 0).getDate()}` },
    { key: "this_quarter", label: "Ce trimestre", start: `${year}-${String(Math.floor(month / 3) * 3 + 1).padStart(2, '0')}-01`, end: format(now, "yyyy-MM-dd") },
    { key: "this_year", label: "Cette année", start: `${year}-01-01`, end: `${year}-12-31` },
    { key: "last_year", label: `${year - 1}`, start: `${year - 1}-01-01`, end: `${year - 1}-12-31` },
    { key: "12_months", label: "12 mois", start: format(new Date(now.setMonth(now.getMonth() - 12)), "yyyy-MM-dd"), end: format(new Date(), "yyyy-MM-dd") },
    { key: "all", label: "Tout", start: "2020-01-01", end: `${year}-12-31` },
  ];
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {presets.map(p => (
        <button 
          key={p.key}
          onClick={() => onSelect(p.start, p.end, p.key)}
          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all duration-200 ${
            activePreset === p.key 
              ? 'bg-[#1E40AF] text-white border-[#1E40AF] shadow-sm' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-[#1E40AF] hover:text-[#1E40AF]'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

// ==================== FILTER CHIP ====================
const FilterChip = ({ label, value, onRemove }) => (
  <Badge variant="secondary" className="gap-1 pr-1">
    <span className="text-xs text-muted-foreground">{label}:</span>
    <span className="font-medium">{value}</span>
    <button onClick={onRemove} className="ml-1 hover:bg-gray-200 rounded-full p-0.5">
      <X className="w-3 h-3" />
    </button>
  </Badge>
);

// ==================== EXPORT FUNCTIONS ====================
const exportToPNG = async (elementId, filename) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  try {
    const canvas = await html2canvas(element, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Image exportée');
  } catch (e) {
    toast.error("Erreur d'export");
  }
};

const exportToCSV = (data, filename, headers) => {
  if (!data?.length) return;
  const csvRows = [headers.join(';')];
  data.forEach(row => {
    csvRows.push(headers.map(h => row[h] ?? '').join(';'));
  });
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  toast.success('CSV exporté');
};

// ==================== CHART CARD ====================
const ChartCard = ({ id, title, icon: Icon, children, data, csvHeaders, className = "" }) => (
  <Card className={`bg-white border ${className}`}>
    <CardHeader className="pb-2 flex flex-row items-center justify-between">
      <CardTitle className="text-base font-semibold flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-blue-600" />}
        {title}
      </CardTitle>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => exportToPNG(id, title.replace(/\s/g, '_'))}>
          <Image className="w-3.5 h-3.5" />
        </Button>
        {data && csvHeaders && (
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => exportToCSV(data, title.replace(/\s/g, '_'), csvHeaders)}>
            <Download className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent id={id}>{children}</CardContent>
  </Card>
);

// ==================== CUSTOM TOOLTIP (Enhanced) ====================
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  
  // Calculate totals for TTC display
  const caHT = payload.find(p => p.dataKey === 'factures_ht')?.value || payload.find(p => p.name === 'CA HT')?.value || 0;
  const caTTC = caHT * 1.2; // Approximate TTC
  const nbFactures = payload.find(p => p.dataKey === 'factures_count')?.value;
  
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-4 text-sm min-w-[180px]">
      <p className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex justify-between gap-6 py-1" style={{ color: entry.color || entry.fill }}>
          <span className="text-gray-600">{entry.name}</span>
          <span className="font-mono font-semibold">{formatCurrency(entry.value)}</span>
        </div>
      ))}
      {caHT > 0 && !payload.find(p => p.name === 'CA TTC') && (
        <div className="flex justify-between gap-6 py-1 text-gray-500 border-t border-gray-100 mt-2 pt-2">
          <span>CA TTC (est.)</span>
          <span className="font-mono">{formatCurrency(caTTC)}</span>
        </div>
      )}
      {nbFactures && (
        <div className="flex justify-between gap-6 py-1 text-gray-500">
          <span>Factures</span>
          <span className="font-mono">{nbFactures}</span>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function AnalyseCA() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // ===== GLOBAL FILTERS =====
  const [dateRange, setDateRange] = useState({ start: `${new Date().getFullYear()}-01-01`, end: `${new Date().getFullYear()}-12-31` });
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [granularity, setGranularity] = useState("monthly");
  const [activePreset, setActivePreset] = useState("this_year");
  const [showTTC, setShowTTC] = useState(false);
  
  // ===== COMPARISON =====
  const [periodA, setPeriodA] = useState({ start: "", end: "" });
  const [periodB, setPeriodB] = useState({ start: "", end: "" });
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  
  // ===== DATA =====
  const [rawData, setRawData] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [projectionData, setProjectionData] = useState(null);
  const [categoryEvolutionData, setCategoryEvolutionData] = useState([]);
  
  // ===== CLIENT DRILL-DOWN =====
  const [selectedClientForDrilldown, setSelectedClientForDrilldown] = useState("");
  const [clientDrilldownData, setClientDrilldownData] = useState([]);

  // Fetch base data
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    Promise.all([
      axios.get(`${API}/stats/top-clients?limit=100`, { headers }),
      axios.get(`${API}/stats/categories`, { headers }),
      axios.get(`${API}/stats/heatmap`, { headers }),
      axios.get(`${API}/stats/growth`, { headers }),
      axios.get(`${API}/stats/projection`, { headers }),
    ]).then(([clientsRes, catRes, heatRes, growthRes, projRes]) => {
      setAllClients(clientsRes.data);
      setAllCategories(catRes.data);
      setHeatmapData(heatRes.data);
      setGrowthData(growthRes.data);
      setProjectionData(projRes.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [token]);

  // Fetch filtered data when filters change
  const fetchFilteredData = useCallback(async () => {
    if (!token || !dateRange.start || !dateRange.end) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const params = new URLSearchParams();
      params.append('start_date', dateRange.start);
      params.append('end_date', dateRange.end);
      params.append('granularity', granularity);
      
      // Fetch both advanced stats and category evolution in parallel
      const [advancedRes, categoryRes] = await Promise.all([
        axios.get(`${API}/stats/advanced?${params}`, { headers }),
        axios.get(`${API}/stats/categories-by-period?${params}`, { headers })
      ]);
      
      setRawData(advancedRes.data);
      setCategoryEvolutionData(categoryRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [token, dateRange, granularity]);

  useEffect(() => { fetchFilteredData(); }, [fetchFilteredData]);

  // Fetch comparison data
  const fetchComparison = async () => {
    if (!periodA.start || !periodA.end || !periodB.start || !periodB.end) {
      toast.error("Veuillez sélectionner les deux périodes à comparer");
      return;
    }
    
    setComparisonLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Fetch detailed data for both periods
      const [dataA, dataB] = await Promise.all([
        axios.get(`${API}/invoices?start_date=${periodA.start}&end_date=${periodA.end}&limit=10000`, { headers }),
        axios.get(`${API}/invoices?start_date=${periodB.start}&end_date=${periodB.end}&limit=10000`, { headers })
      ]);
      
      // Process period A
      const periodAInvoices = dataA.data.invoices || [];
      const periodAStats = processInvoices(periodAInvoices);
      
      // Process period B  
      const periodBInvoices = dataB.data.invoices || [];
      const periodBStats = processInvoices(periodBInvoices);
      
      setComparisonResult({
        periodA: { ...periodAStats, invoices: periodAInvoices, dates: periodA },
        periodB: { ...periodBStats, invoices: periodBInvoices, dates: periodB }
      });
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la comparaison");
    } finally {
      setComparisonLoading(false);
    }
  };

  // Process invoices for stats
  const processInvoices = (invoices) => {
    const factures = invoices.filter(i => i.type === 'Facture');
    const avoirs = invoices.filter(i => i.type === 'Avoir');
    
    const totalHT = factures.reduce((s, i) => s + (i.total_ht || 0), 0);
    const totalAvoirs = avoirs.reduce((s, i) => s + Math.abs(i.total_ht || 0), 0);
    
    // Group by client
    const byClient = {};
    factures.forEach(i => {
      if (!byClient[i.client]) byClient[i.client] = { total: 0, count: 0 };
      byClient[i.client].total += i.total_ht || 0;
      byClient[i.client].count++;
    });
    
    // Group by category (from lines)
    const byCategory = {};
    factures.forEach(i => {
      const cat = categorizeInvoice(i);
      if (!byCategory[cat]) byCategory[cat] = { total: 0, count: 0 };
      byCategory[cat].total += i.total_ht || 0;
      byCategory[cat].count++;
    });
    
    // Top products/descriptions
    const byDescription = {};
    factures.forEach(i => {
      if (i.lines?.length) {
        i.lines.forEach(line => {
          const desc = line.description || 'N/A';
          if (!byDescription[desc]) byDescription[desc] = { total: 0, count: 0 };
          byDescription[desc].total += line.total_ht || 0;
          byDescription[desc].count++;
        });
      }
    });
    
    return {
      totalHT,
      totalAvoirs,
      netHT: totalHT - totalAvoirs,
      facturesCount: factures.length,
      avgBasket: factures.length > 0 ? totalHT / factures.length : 0,
      byClient: Object.entries(byClient).map(([k, v]) => ({ name: k, ...v })).sort((a, b) => b.total - a.total),
      byCategory: Object.entries(byCategory).map(([k, v]) => ({ name: k, ...v })).sort((a, b) => b.total - a.total),
      byDescription: Object.entries(byDescription).map(([k, v]) => ({ name: k, ...v })).sort((a, b) => b.total - a.total).slice(0, 20)
    };
  };

  // Categorize invoice based on description
  const categorizeInvoice = (invoice) => {
    const desc = (invoice.lines?.[0]?.description || '').toLowerCase();
    if (desc.includes('hébergement') || desc.includes('sauvegarde') || desc.includes('backup') || desc.includes('box') || desc.includes('cloud')) return 'Hébergement/Sauvegarde';
    if (desc.includes('fibre') || desc.includes('ftth') || desc.includes('internet')) return 'Fibre/Internet';
    if (desc.includes('téléphone') || desc.includes('4g')) return 'Téléphonie';
    if (desc.includes('commission')) return 'Commission';
    if (desc.includes('installation') || desc.includes('deploiement')) return 'Installation';
    if (desc.includes('materiel') || desc.includes('cartouche')) return 'Matériel';
    return 'Autre';
  };

  // Fetch client drill-down
  useEffect(() => {
    if (!selectedClientForDrilldown || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/stats/client-evolution?client_name=${encodeURIComponent(selectedClientForDrilldown)}&months=36`, { headers })
      .then(res => setClientDrilldownData(res.data))
      .catch(() => {});
  }, [selectedClientForDrilldown, token]);

  // Reset all filters
  const resetFilters = () => {
    setDateRange({ start: `${new Date().getFullYear()}-01-01`, end: `${new Date().getFullYear()}-12-31` });
    setSelectedClients([]);
    setSelectedCategories([]);
    setGranularity("monthly");
    setPeriodA({ start: "", end: "" });
    setPeriodB({ start: "", end: "" });
    setComparisonResult(null);
  };

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalCA = rawData.reduce((s, d) => s + (d.factures_ht || 0), 0);
    const totalAvoirs = rawData.reduce((s, d) => s + Math.abs(d.avoirs_ht || 0), 0);
    const totalCount = rawData.reduce((s, d) => s + (d.factures_count || 0), 0);
    return {
      totalCA, totalAvoirs,
      netCA: totalCA - totalAvoirs,
      totalCount,
      avgBasket: totalCount > 0 ? totalCA / totalCount : 0
    };
  }, [rawData]);

  // Heatmap grid
  const heatmapGrid = useMemo(() => {
    const years = [...new Set(heatmapData.map(d => d.year))].sort();
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const maxVal = Math.max(...heatmapData.map(d => d.total_ht), 1);
    
    return months.map((month, idx) => {
      const monthNum = String(idx + 1).padStart(2, '0');
      const row = { month };
      years.forEach(year => {
        const data = heatmapData.find(d => d.year === year && d.month === monthNum);
        row[year] = data?.total_ht || 0;
        row[`${year}_intensity`] = data ? data.total_ht / maxVal : 0;
      });
      return row;
    });
  }, [heatmapData]);

  const heatmapYears = useMemo(() => [...new Set(heatmapData.map(d => d.year))].sort(), [heatmapData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-80 skeleton rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="analyse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Analyse du CA</h1>
          <p className="text-muted-foreground text-sm">Explorez vos données librement</p>
        </div>
        <Button variant="outline" onClick={resetFilters} className="gap-2 w-full sm:w-auto">
          <RotateCcw className="w-4 h-4" /> Réinitialiser
        </Button>
      </div>

      {/* Global Filter Bar - Modern & Compact */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-4 sm:p-5 space-y-5">
          {/* Row 1: Date Range + Quick Presets */}
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <DateRangePicker
              label="Période d'analyse"
              startDate={dateRange.start}
              endDate={dateRange.end}
              onStartChange={(d) => { setDateRange(p => ({ ...p, start: d })); setActivePreset(null); }}
              onEndChange={(d) => { setDateRange(p => ({ ...p, end: d })); setActivePreset(null); }}
            />
            <div className="flex-1">
              <span className="text-xs font-medium text-gray-500 block mb-2">Raccourcis</span>
              <QuickDateButtons 
                activePreset={activePreset}
                onSelect={(s, e, key) => { setDateRange({ start: s, end: e }); setActivePreset(key); }} 
              />
            </div>
          </div>
          
          {/* Row 2: Granularity + Filters */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 pt-4 border-t border-gray-100">
            <div>
              <span className="text-xs font-medium text-gray-500 block mb-2">Granularité</span>
              <div className="flex rounded-full border border-gray-200 overflow-hidden bg-gray-50 p-0.5">
                {[{v: 'monthly', l: 'Mois'}, {v: 'quarterly', l: 'Trimestre'}, {v: 'yearly', l: 'Année'}].map(({v, l}) => (
                  <button
                    key={v}
                    onClick={() => setGranularity(v)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all ${
                      granularity === v 
                        ? 'bg-[#1E40AF] text-white shadow-sm' 
                        : 'text-gray-600 hover:text-[#1E40AF]'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <span className="text-xs font-medium text-gray-500 block mb-2">Client</span>
              <MultiSelectFilter
                values={selectedClients}
                onValuesChange={setSelectedClients}
                options={allClients.slice(0, 100).map(c => c.client)}
                allLabel="Tous les clients"
                placeholder="Rechercher un client..."
                className="w-52 bg-white border-gray-200 rounded-lg"
              />
            </div>
            
            <div>
              <span className="text-xs font-medium text-gray-500 block mb-2">Catégorie</span>
              <MultiSelectFilter
                values={selectedCategories}
                onValuesChange={setSelectedCategories}
                options={allCategories.map(c => c.category)}
                allLabel="Toutes catégories"
                placeholder="Rechercher..."
                className="w-52 bg-white border-gray-200 rounded-lg"
              />
            </div>
          </div>
          
          {/* Active Filters Tags */}
          {(selectedClients.length > 0 || selectedCategories.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400 font-medium">Filtres actifs:</span>
              {selectedClients.map(c => (
                <FilterChip key={c} label="Client" value={c} onRemove={() => setSelectedClients([])} />
              ))}
              {selectedCategories.map(c => (
                <FilterChip key={c} label="Catégorie" value={c} onRemove={() => setSelectedCategories([])} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Summary - Modern Cards with Sparklines */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {[
          { 
            label: "CA HT", 
            value: summary.totalCA === 0 ? "—" : formatCurrency(summary.totalCA), 
            color: "text-[#1E40AF]", 
            bg: "from-blue-50 to-indigo-50",
            borderColor: "border-blue-200",
            trend: rawData.length >= 2 ? ((rawData[rawData.length-1]?.factures_ht || 0) - (rawData[rawData.length-2]?.factures_ht || 0)) / Math.max(rawData[rawData.length-2]?.factures_ht || 1, 1) * 100 : null,
            sparkData: rawData.slice(-6).map(d => d.factures_ht || 0)
          },
          { 
            label: "Avoirs", 
            value: summary.totalAvoirs === 0 ? "—" : `-${formatCurrency(summary.totalAvoirs)}`, 
            color: "text-red-500", 
            bg: "from-red-50 to-rose-50",
            borderColor: "border-red-200",
            trend: null,
            sparkData: rawData.slice(-6).map(d => Math.abs(d.avoirs_ht || 0))
          },
          { 
            label: "CA Net", 
            value: summary.netCA === 0 ? "—" : formatCurrency(summary.netCA), 
            color: "text-emerald-600", 
            bg: "from-emerald-50 to-green-50",
            borderColor: "border-emerald-200",
            trend: rawData.length >= 2 ? ((rawData[rawData.length-1]?.net_ht || 0) - (rawData[rawData.length-2]?.net_ht || 0)) / Math.max(rawData[rawData.length-2]?.net_ht || 1, 1) * 100 : null,
            sparkData: rawData.slice(-6).map(d => d.net_ht || 0)
          },
          { 
            label: "Factures", 
            value: summary.totalCount, 
            color: "text-purple-600", 
            bg: "from-purple-50 to-violet-50",
            borderColor: "border-purple-200",
            trend: rawData.length >= 2 ? ((rawData[rawData.length-1]?.factures_count || 0) - (rawData[rawData.length-2]?.factures_count || 0)) / Math.max(rawData[rawData.length-2]?.factures_count || 1, 1) * 100 : null,
            sparkData: rawData.slice(-6).map(d => d.factures_count || 0)
          },
          { 
            label: "Panier moyen", 
            value: summary.avgBasket === 0 ? "—" : formatCurrency(summary.avgBasket), 
            color: "text-orange-600", 
            bg: "from-orange-50 to-amber-50",
            borderColor: "border-orange-200",
            trend: null,
            sparkData: []
          }
        ].map(({ label, value, color, bg, borderColor, trend, sparkData }) => (
          <Card key={label} className={`bg-gradient-to-br ${bg} border ${borderColor} overflow-hidden`}>
            <CardContent className="p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className={`text-lg sm:text-2xl font-bold font-mono ${color}`}>{value}</p>
                {trend !== null && trend !== 0 && !isNaN(trend) && isFinite(trend) && (
                  <span className={`text-xs font-medium flex items-center ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(trend).toFixed(0)}%
                  </span>
                )}
              </div>
              {/* Mini Sparkline */}
              {sparkData.length > 0 && (
                <div className="mt-2 h-6 flex items-end gap-0.5">
                  {sparkData.map((val, i) => {
                    const max = Math.max(...sparkData, 1);
                    const height = (val / max) * 100;
                    return (
                      <div 
                        key={i} 
                        className={`flex-1 rounded-t ${i === sparkData.length - 1 ? 'bg-[#1E40AF]' : 'bg-gray-300'}`}
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs for different views - Modern Style */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-transparent border-b border-gray-200 p-0 h-auto w-full justify-start gap-0 rounded-none">
          {[
            { value: "overview", label: "Vue d'ensemble" },
            { value: "products", label: "Produits" },
            { value: "comparison", label: "Comparaison" },
            { value: "clients", label: "Clients" },
            { value: "trends", label: "Tendances" }
          ].map(tab => (
            <TabsTrigger 
              key={tab.value}
              value={tab.value} 
              className={`px-4 py-3 text-sm font-medium rounded-none border-b-2 transition-all data-[state=active]:border-[#1E40AF] data-[state=active]:text-[#1E40AF] data-[state=active]:bg-transparent data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-500 hover:text-[#1E40AF]`}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview" className="space-y-6">
          {/* Evolution Chart - Modern Design */}
          <Card className="bg-white border shadow-sm overflow-hidden">
            <CardHeader className="pb-2 flex flex-row items-center justify-between border-b bg-gray-50/50">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#1E40AF]" />
                Évolution du CA
              </CardTitle>
              <div className="flex items-center gap-3">
                {/* HT/TTC Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setShowTTC(false)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${!showTTC ? 'bg-white shadow-sm text-[#1E40AF]' : 'text-gray-500'}`}
                  >
                    HT
                  </button>
                  <button
                    onClick={() => setShowTTC(true)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${showTTC ? 'bg-white shadow-sm text-[#1E40AF]' : 'text-gray-500'}`}
                  >
                    TTC
                  </button>
                </div>
                {/* Export buttons */}
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-500 hover:text-[#1E40AF]" onClick={() => exportToPNG('chart-evolution-main', 'Evolution_CA')}>
                    <Image className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-500 hover:text-[#1E40AF]" onClick={() => exportToCSV(rawData, 'Evolution_CA', ['period', 'factures_ht', 'net_ht'])}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6" id="chart-evolution-main">
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={rawData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1E40AF" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis 
                      dataKey="period" 
                      stroke="#9ca3af" 
                      fontSize={11} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      stroke="#9ca3af" 
                      fontSize={11} 
                      tickFormatter={formatK} 
                      width={50}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', paddingTop: '15px' }}
                      iconType="circle"
                    />
                    {/* Average Line */}
                    {rawData.length > 0 && (
                      <ReferenceLine 
                        y={rawData.reduce((s, d) => s + (showTTC ? (d.factures_ht || 0) * 1.2 : (d.factures_ht || 0)), 0) / rawData.length} 
                        stroke="#9ca3af" 
                        strokeDasharray="5 5" 
                        strokeWidth={1.5}
                        label={{ value: 'Moyenne', position: 'right', fontSize: 10, fill: '#9ca3af' }}
                      />
                    )}
                    {/* Elegant Bars */}
                    <Bar 
                      dataKey={showTTC ? "factures_ttc" : "factures_ht"} 
                      name={showTTC ? "CA TTC" : "CA HT"} 
                      fill="url(#barGradient)" 
                      radius={[6, 6, 0, 0]}
                      maxBarSize={50}
                    />
                    {/* Trend Line */}
                    <Line 
                      type="monotone" 
                      dataKey={showTTC ? "net_ttc" : "net_ht"} 
                      name={showTTC ? "Net TTC" : "Net HT"} 
                      stroke="#10b981" 
                      strokeWidth={2.5}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4, stroke: '#fff' }}
                      activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Categories Pie */}
            <ChartCard id="chart-categories" title="Répartition par prestation" icon={PieChart} data={allCategories} csvHeaders={['category', 'total_ht']}>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={allCategories}
                      dataKey="total_ht"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={typeof window !== 'undefined' && window.innerWidth < 640 ? 60 : 80}
                      label={({ category, percent }) => `${category.substring(0, 8)}... (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                      fontSize={10}
                    >
                      {allCategories.map((entry, i) => (
                        <Cell 
                          key={i} 
                          fill={CATEGORY_COLORS[entry.category] || PIE_COLORS[i % PIE_COLORS.length]} 
                          className="cursor-pointer hover:opacity-80"
                          onClick={() => setSelectedCategories([entry.category])}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-2">Cliquez sur une part pour filtrer</p>
            </ChartCard>

            {/* Heatmap */}
            <ChartCard id="chart-heatmap" title="Saisonnalité" icon={Flame}>
              <ScrollArea className="h-72">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th className="text-left py-1 px-2 font-medium">Mois</th>
                      {heatmapYears.map(y => <th key={y} className="text-center py-1 px-1 font-medium">{y}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapGrid.map((row, i) => (
                      <tr key={i}>
                        <td className="py-1 px-2 font-medium">{row.month}</td>
                        {heatmapYears.map(year => {
                          const intensity = row[`${year}_intensity`] || 0;
                          const bg = intensity > 0.8 ? 'bg-blue-700 text-white' :
                                     intensity > 0.6 ? 'bg-blue-500 text-white' :
                                     intensity > 0.4 ? 'bg-blue-300' :
                                     intensity > 0.2 ? 'bg-blue-100' : 'bg-gray-50';
                          return (
                            <td 
                              key={year} 
                              className={`py-1 px-1 text-center font-mono rounded cursor-pointer hover:ring-2 hover:ring-blue-400 ${bg}`}
                              title={`${row.month} ${year}: ${formatCurrency(row[year])}`}
                              onClick={() => {
                                const monthIdx = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'].indexOf(row.month) + 1;
                                const monthStr = String(monthIdx).padStart(2, '0');
                                const lastDay = new Date(parseInt(year), monthIdx, 0).getDate();
                                setDateRange({ start: `${year}-${monthStr}-01`, end: `${year}-${monthStr}-${lastDay}` });
                              }}
                            >
                              {row[year] > 0 ? formatK(row[year]) : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Snowflake className="w-3 h-3" /> Faible</span>
                <span>Cliquez sur une cellule pour filtrer ce mois</span>
                <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> Élevé</span>
              </div>
            </ChartCard>
          </div>
        </TabsContent>

        {/* ===== PRODUCTS TAB ===== */}
        <TabsContent value="products" className="space-y-6">
          {/* Category Evolution - Stacked Bar Chart */}
          <ChartCard 
            id="chart-category-stacked" 
            title="Évolution du CA par catégorie (Barres empilées)" 
            icon={Package}
            data={categoryEvolutionData}
            csvHeaders={['period', 'Hébergement/Sauvegarde', 'Fibre/Internet', 'Téléphonie', 'Matériel', 'Commission', 'Installation', 'Autre']}
          >
            <div className="h-80">
              {categoryEvolutionData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryEvolutionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#6b7280" 
                      fontSize={10} 
                      angle={-45} 
                      textAnchor="end" 
                      height={60}
                    />
                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={formatK} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const total = payload.reduce((s, p) => s + (p.value || 0), 0);
                        return (
                          <div className="bg-white border rounded-lg shadow-lg p-3 text-sm max-w-xs">
                            <p className="font-medium text-foreground mb-2">{label}</p>
                            <p className="font-bold border-b pb-1 mb-1">Total: {formatCurrency(total)}</p>
                            {payload.filter(p => p.value > 0).sort((a, b) => b.value - a.value).map((entry, i) => (
                              <div key={i} className="flex justify-between gap-4" style={{ color: entry.color }}>
                                <span>{entry.name}:</span>
                                <span className="font-mono font-medium">{formatCurrency(entry.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Hébergement/Sauvegarde" stackId="a" fill={CATEGORY_COLORS['Hébergement/Sauvegarde']} name="Hébergement/Sauvegarde" />
                    <Bar dataKey="Fibre/Internet" stackId="a" fill={CATEGORY_COLORS['Fibre/Internet']} name="Fibre/Internet" />
                    <Bar dataKey="Téléphonie" stackId="a" fill={CATEGORY_COLORS['Téléphonie']} name="Téléphonie" />
                    <Bar dataKey="Matériel" stackId="a" fill={CATEGORY_COLORS['Matériel']} name="Matériel" />
                    <Bar dataKey="Commission" stackId="a" fill={CATEGORY_COLORS['Commission']} name="Commission" />
                    <Bar dataKey="Installation" stackId="a" fill={CATEGORY_COLORS['Installation']} name="Installation" />
                    <Bar dataKey="Autre" stackId="a" fill={CATEGORY_COLORS['Autre']} name="Autre" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <p>Aucune donnée pour cette période</p>
                </div>
              )}
            </div>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Visualisez la composition du CA total par période - chaque segment représente une catégorie de produits
            </p>
          </ChartCard>

          {/* Category Evolution - Line Chart */}
          <ChartCard 
            id="chart-category-lines" 
            title="Évolution du CA par catégorie (Lignes)" 
            icon={TrendingUp}
            data={categoryEvolutionData}
            csvHeaders={['period', 'Hébergement/Sauvegarde', 'Fibre/Internet', 'Téléphonie', 'Matériel', 'Commission', 'Installation', 'Autre']}
          >
            <div className="h-80">
              {categoryEvolutionData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={categoryEvolutionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#6b7280" 
                      fontSize={10} 
                      angle={-45} 
                      textAnchor="end" 
                      height={60}
                    />
                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={formatK} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="Hébergement/Sauvegarde" stroke={CATEGORY_COLORS['Hébergement/Sauvegarde']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Fibre/Internet" stroke={CATEGORY_COLORS['Fibre/Internet']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Téléphonie" stroke={CATEGORY_COLORS['Téléphonie']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Matériel" stroke={CATEGORY_COLORS['Matériel']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Commission" stroke={CATEGORY_COLORS['Commission']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Installation" stroke={CATEGORY_COLORS['Installation']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="Autre" stroke={CATEGORY_COLORS['Autre']} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <p>Aucune donnée pour cette période</p>
                </div>
              )}
            </div>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Comparez facilement l'évolution de chaque catégorie - cliquez sur la légende pour afficher/masquer une catégorie
            </p>
          </ChartCard>

          {/* Category Summary Table */}
          <ChartCard id="chart-category-table" title="Résumé par catégorie" icon={Layers}>
            <ScrollArea className="h-64">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Catégorie</th>
                    <th className="text-right py-2 px-2">CA Total</th>
                    <th className="text-right py-2 px-2">% du CA</th>
                    <th className="text-right py-2 px-2">Tendance</th>
                    <th className="text-right py-2 px-2">Mois actifs</th>
                    <th className="text-right py-2 px-2">Moyenne/mois</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Calculate totals per category with trend
                    const categories = ['Hébergement/Sauvegarde', 'Fibre/Internet', 'Téléphonie', 'Matériel', 'Commission', 'Installation', 'Autre'];
                    const categoryStats = categories.map(cat => {
                      let total = 0;
                      let monthsActive = 0;
                      let lastPeriodValue = 0;
                      let prevPeriodValue = 0;
                      
                      categoryEvolutionData.forEach((period, idx) => {
                        const val = period[cat] || 0;
                        total += val;
                        if (val > 0) monthsActive++;
                        
                        // Get last two periods for trend calculation
                        if (idx === categoryEvolutionData.length - 1) {
                          lastPeriodValue = val;
                        } else if (idx === categoryEvolutionData.length - 2) {
                          prevPeriodValue = val;
                        }
                      });
                      
                      // Calculate trend percentage
                      let trend = null;
                      if (prevPeriodValue > 0) {
                        trend = ((lastPeriodValue - prevPeriodValue) / prevPeriodValue) * 100;
                      } else if (lastPeriodValue > 0) {
                        trend = 100; // New category
                      }
                      
                      return { 
                        category: cat, 
                        total, 
                        monthsActive, 
                        avg: monthsActive > 0 ? total / monthsActive : 0,
                        trend,
                        lastPeriodValue,
                        prevPeriodValue
                      };
                    }).filter(c => c.total > 0).sort((a, b) => b.total - a.total);
                    
                    const grandTotal = categoryStats.reduce((s, c) => s + c.total, 0);
                    
                    return categoryStats.map((stat, i) => (
                      <tr key={stat.category} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[stat.category] || '#64748b' }} />
                          {stat.category}
                        </td>
                        <td className="text-right py-2 px-2 font-mono font-bold">{formatCurrency(stat.total)}</td>
                        <td className="text-right py-2 px-2 font-mono">{grandTotal > 0 ? ((stat.total / grandTotal) * 100).toFixed(1) : 0}%</td>
                        <td className="text-right py-2 px-2">
                          {stat.trend !== null ? (
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              stat.trend > 5 ? 'bg-green-100 text-green-700' : 
                              stat.trend < -5 ? 'bg-red-100 text-red-700' : 
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {stat.trend > 5 ? <ArrowUp className="w-3 h-3" /> : 
                               stat.trend < -5 ? <ArrowDown className="w-3 h-3" /> : 
                               <Minus className="w-3 h-3" />}
                              {stat.trend > 0 ? '+' : ''}{stat.trend.toFixed(0)}%
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="text-right py-2 px-2">{stat.monthsActive} / {categoryEvolutionData.length}</td>
                        <td className="text-right py-2 px-2 font-mono">{formatCurrency(stat.avg)}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </ScrollArea>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Tendance = variation entre la dernière et l'avant-dernière période
            </p>
          </ChartCard>
        </TabsContent>

        {/* ===== COMPARISON TAB ===== */}
        <TabsContent value="comparison" className="space-y-6">
          <Card className="bg-white border">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-blue-600" />
                Comparer deux périodes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-700">Période A</p>
                  <DateRangePicker
                    startDate={periodA.start}
                    endDate={periodA.end}
                    onStartChange={(d) => setPeriodA(p => ({ ...p, start: d }))}
                    onEndChange={(d) => setPeriodA(p => ({ ...p, end: d }))}
                  />
                  <QuickDateButtons onSelect={(s, e) => setPeriodA({ start: s, end: e })} />
                </div>
                
                <div className="space-y-2 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm font-semibold text-green-700">Période B</p>
                  <DateRangePicker
                    startDate={periodB.start}
                    endDate={periodB.end}
                    onStartChange={(d) => setPeriodB(p => ({ ...p, start: d }))}
                    onEndChange={(d) => setPeriodB(p => ({ ...p, end: d }))}
                  />
                  <QuickDateButtons onSelect={(s, e) => setPeriodB({ start: s, end: e })} />
                </div>
                
                <div className="flex items-center justify-center">
                  <Button 
                    onClick={fetchComparison} 
                    disabled={comparisonLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
                  >
                    {comparisonLoading ? "Analyse..." : "Comparer"}
                  </Button>
                </div>
              </div>

              {/* Comparison Results */}
              {comparisonResult && (
                <div className="space-y-6 pt-6 border-t">
                  {/* Summary Comparison */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-700 mb-1">
                        Période A: {formatDateFR(periodA.start)} → {formatDateFR(periodA.end)}
                      </p>
                      <p className="text-3xl font-bold font-mono text-blue-600">{formatCurrency(comparisonResult.periodA.totalHT)}</p>
                      <p className="text-sm text-muted-foreground">{comparisonResult.periodA.facturesCount} factures • Panier: {formatCurrency(comparisonResult.periodA.avgBasket)}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm font-medium text-green-700 mb-1">
                        Période B: {formatDateFR(periodB.start)} → {formatDateFR(periodB.end)}
                      </p>
                      <p className="text-3xl font-bold font-mono text-green-600">{formatCurrency(comparisonResult.periodB.totalHT)}</p>
                      <p className="text-sm text-muted-foreground">{comparisonResult.periodB.facturesCount} factures • Panier: {formatCurrency(comparisonResult.periodB.avgBasket)}</p>
                      {comparisonResult.periodA.totalHT > 0 && (
                        <Badge className={`mt-2 ${((comparisonResult.periodB.totalHT - comparisonResult.periodA.totalHT) / comparisonResult.periodA.totalHT) >= 0 ? 'bg-green-600' : 'bg-red-500'}`}>
                          {formatPercent(((comparisonResult.periodB.totalHT - comparisonResult.periodA.totalHT) / comparisonResult.periodA.totalHT) * 100)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Detailed Comparison Tables */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* By Client */}
                    <Card className="border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Top Clients - Comparaison</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-64">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="text-left py-2">Client</th>
                                <th className="text-right py-2 text-blue-600">Période A</th>
                                <th className="text-right py-2 text-green-600">Période B</th>
                                <th className="text-right py-2">Δ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const allClients = new Set([
                                  ...comparisonResult.periodA.byClient.map(c => c.name),
                                  ...comparisonResult.periodB.byClient.map(c => c.name)
                                ]);
                                return [...allClients].slice(0, 15).map(client => {
                                  const a = comparisonResult.periodA.byClient.find(c => c.name === client)?.total || 0;
                                  const b = comparisonResult.periodB.byClient.find(c => c.name === client)?.total || 0;
                                  const diff = a > 0 ? ((b - a) / a) * 100 : (b > 0 ? 100 : 0);
                                  return (
                                    <tr key={client} className="border-b hover:bg-gray-50">
                                      <td className="py-2 truncate max-w-[120px]" title={client}>{client}</td>
                                      <td className="text-right py-2 font-mono text-blue-600">{formatK(a)}</td>
                                      <td className="text-right py-2 font-mono text-green-600">{formatK(b)}</td>
                                      <td className={`text-right py-2 font-mono ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {diff !== 0 ? formatPercent(diff) : '-'}
                                      </td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </ScrollArea>
                      </CardContent>
                    </Card>

                    {/* By Category */}
                    <Card className="border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Par Catégorie - Comparaison</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-64">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="text-left py-2">Catégorie</th>
                                <th className="text-right py-2 text-blue-600">Période A</th>
                                <th className="text-right py-2 text-green-600">Période B</th>
                                <th className="text-right py-2">Δ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const allCats = new Set([
                                  ...comparisonResult.periodA.byCategory.map(c => c.name),
                                  ...comparisonResult.periodB.byCategory.map(c => c.name)
                                ]);
                                return [...allCats].map(cat => {
                                  const a = comparisonResult.periodA.byCategory.find(c => c.name === cat)?.total || 0;
                                  const b = comparisonResult.periodB.byCategory.find(c => c.name === cat)?.total || 0;
                                  const diff = a > 0 ? ((b - a) / a) * 100 : (b > 0 ? 100 : 0);
                                  return (
                                    <tr key={cat} className="border-b hover:bg-gray-50">
                                      <td className="py-2 flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#64748b' }} />
                                        {cat}
                                      </td>
                                      <td className="text-right py-2 font-mono text-blue-600">{formatK(a)}</td>
                                      <td className="text-right py-2 font-mono text-green-600">{formatK(b)}</td>
                                      <td className={`text-right py-2 font-mono ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {diff !== 0 ? formatPercent(diff) : '-'}
                                      </td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Products Comparison */}
                  <Card className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Top Produits/Services vendus - Comparaison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-blue-600 mb-2">Période A</p>
                          <ScrollArea className="h-48">
                            <table className="w-full text-xs">
                              <tbody>
                                {comparisonResult.periodA.byDescription.slice(0, 10).map((item, i) => (
                                  <tr key={i} className="border-b">
                                    <td className="py-1.5 truncate max-w-[200px]" title={item.name}>{item.name}</td>
                                    <td className="text-right py-1.5 font-mono">{formatK(item.total)}</td>
                                    <td className="text-right py-1.5 text-muted-foreground">({item.count})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </ScrollArea>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-600 mb-2">Période B</p>
                          <ScrollArea className="h-48">
                            <table className="w-full text-xs">
                              <tbody>
                                {comparisonResult.periodB.byDescription.slice(0, 10).map((item, i) => (
                                  <tr key={i} className="border-b">
                                    <td className="py-1.5 truncate max-w-[200px]" title={item.name}>{item.name}</td>
                                    <td className="text-right py-1.5 font-mono">{formatK(item.total)}</td>
                                    <td className="text-right py-1.5 text-muted-foreground">({item.count})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </ScrollArea>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CLIENTS TAB ===== */}
        <TabsContent value="clients" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clients */}
            <ChartCard id="chart-top-clients" title="Top Clients" icon={Building2} data={allClients.slice(0, 15)} csvHeaders={['client', 'total_ht', 'count']}>
              <ScrollArea className="h-80">
                {allClients.slice(0, 20).map((client, i) => {
                  const maxCA = allClients[0]?.total_ht || 1;
                  const percent = (client.total_ht / maxCA) * 100;
                  return (
                    <div 
                      key={client.client} 
                      className="group cursor-pointer hover:bg-blue-50 p-2 rounded-lg mb-1"
                      onClick={() => setSelectedClientForDrilldown(client.client)}
                    >
                      <div className="flex justify-between items-center text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">
                            {i + 1}
                          </span>
                          <span className="font-medium truncate max-w-[140px]" title={client.client}>{client.client}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-bold">{formatCurrency(client.total_ht)}</span>
                          <span className="text-xs text-muted-foreground ml-1">({client.count})</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </ScrollArea>
              <p className="text-xs text-center text-muted-foreground mt-2">Cliquez sur un client pour voir son évolution</p>
            </ChartCard>

            {/* Client Evolution */}
            <ChartCard id="chart-client-evolution" title={`Évolution: ${selectedClientForDrilldown || 'Sélectionnez un client'}`} icon={TrendingUp}>
              {selectedClientForDrilldown ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={clientDrilldownData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" fontSize={10} />
                      <YAxis stroke="#6b7280" fontSize={11} tickFormatter={formatK} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="total_ht" name="CA HT" fill="#3b82f6" fillOpacity={0.3} stroke="#3b82f6" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Sélectionnez un client dans la liste pour voir son évolution sur 36 mois</p>
                  </div>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Client Statistics */}
          {selectedClientForDrilldown && clientDrilldownData.length > 0 && (
            <Card className="border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Statistiques de {selectedClientForDrilldown}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">CA Total (36 mois)</p>
                    <p className="text-xl font-bold font-mono text-blue-600">
                      {formatCurrency(clientDrilldownData.reduce((s, d) => s + d.total_ht, 0))}
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Nombre de factures</p>
                    <p className="text-xl font-bold font-mono text-green-600">
                      {clientDrilldownData.reduce((s, d) => s + d.count, 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Mois avec facturation</p>
                    <p className="text-xl font-bold font-mono text-purple-600">
                      {clientDrilldownData.filter(d => d.total_ht > 0).length} / {clientDrilldownData.length}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">CA moyen / mois actif</p>
                    <p className="text-xl font-bold font-mono text-orange-600">
                      {formatCurrency(clientDrilldownData.reduce((s, d) => s + d.total_ht, 0) / Math.max(clientDrilldownData.filter(d => d.total_ht > 0).length, 1))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== TRENDS TAB ===== */}
        <TabsContent value="trends" className="space-y-6">
          {/* Projection Card - Premium Design */}
          {projectionData && (
            <Card className="overflow-hidden border-0 shadow-lg">
              <div className="bg-gradient-to-r from-[#1E40AF] to-emerald-500 p-6 text-white">
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Target className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-semibold">Projection fin d'année {projectionData.year}</h3>
                </div>
                <p className="text-white/70 text-sm ml-12">Basée sur {projectionData.months_elapsed} mois de données</p>
              </div>
              <CardContent className="p-0">
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
                  <div className="p-5 text-center hover:bg-gray-50 transition-colors">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">CA réalisé</p>
                    <p className="text-2xl font-bold font-mono text-[#1E40AF]">{formatCurrency(projectionData.ytd_ca)}</p>
                    <p className="text-xs text-gray-400 mt-1">{projectionData.months_elapsed} mois écoulés</p>
                  </div>
                  <div className="p-5 text-center hover:bg-gray-50 transition-colors">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">CA projeté</p>
                    <p className="text-2xl font-bold font-mono text-emerald-600">{formatCurrency(projectionData.projected_ca)}</p>
                    <p className="text-xs text-gray-400 mt-1">estimation annuelle</p>
                  </div>
                  <div className="p-5 text-center hover:bg-gray-50 transition-colors">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">CA {projectionData.year - 1}</p>
                    <p className="text-2xl font-bold font-mono text-gray-700">{formatCurrency(projectionData.prev_year_ca)}</p>
                    <p className="text-xs text-gray-400 mt-1">année précédente</p>
                  </div>
                  <div className="p-5 text-center hover:bg-gray-50 transition-colors">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Évolution</p>
                    <p className={`text-2xl font-bold font-mono flex items-center justify-center gap-1 ${projectionData.projected_growth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {projectionData.projected_growth >= 0 ? <ArrowUp className="w-5 h-5" /> : <ArrowDown className="w-5 h-5" />}
                      {formatPercent(Math.abs(projectionData.projected_growth))}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">vs {projectionData.year - 1}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Growth Charts - Modern Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* MoM Growth - Enhanced */}
            <Card className="border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 bg-gray-50/50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-[#1E40AF]" />
                    </div>
                    Variation mensuelle
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToPNG('chart-mom', 'Croissance_MoM')}>
                      <Image className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToCSV(growthData, 'Croissance_MoM', ['month', 'total_ht', 'mom'])}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4" id="chart-mom">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={growthData.slice(-24)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="barGradientMom" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1E40AF" stopOpacity={0.9}/>
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.6}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="month" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                      <YAxis yAxisId="left" stroke="#9ca3af" fontSize={10} tickFormatter={formatK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={45} />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" fontSize={10} tickFormatter={v => `${v}%`} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={45} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        formatter={(value, name) => [name === 'Variation %' ? `${value?.toFixed(1)}%` : formatCurrency(value), name]} 
                      />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />
                      <Bar yAxisId="left" dataKey="total_ht" name="CA HT" fill="url(#barGradientMom)" radius={[4, 4, 0, 0]} maxBarSize={35} />
                      <Line yAxisId="right" type="monotone" dataKey="mom" name="Variation %" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 3, strokeWidth: 2, stroke: '#fff' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-center text-gray-400 mt-3">
                  Évolution du CA mois par mois avec le pourcentage de variation
                </p>
              </CardContent>
            </Card>

            {/* Trend with Moving Average - Enhanced */}
            <Card className="border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 bg-gray-50/50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-100 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                    </div>
                    Tendance lissée
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToPNG('chart-ma', 'Tendance_Lissee')}>
                      <Image className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToCSV(growthData.filter(d => d.ma3), 'Tendance_Lissee', ['month', 'total_ht', 'ma3'])}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4" id="chart-ma">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={growthData.slice(-36)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="month" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                      <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={formatK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={45} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        content={<CustomTooltip />} 
                      />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />
                      <Bar dataKey="total_ht" name="CA mensuel" fill="#e5e7eb" radius={[4, 4, 0, 0]} maxBarSize={35} />
                      <Area type="monotone" dataKey="ma3" name="Tendance" fill="url(#areaGradient)" stroke="#10b981" strokeWidth={3} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-center text-gray-400 mt-3">
                  La courbe verte montre la tendance générale (moyenne glissante sur 3 mois)
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
